import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SortDescriptor } from '@react-types/shared';
import { ActionButton, DatePicker, Switch, Text } from "@react-spectrum/s2";
import { toast } from '../../utils/toast';
import { Checkmark, Close } from '../icons/s2';
import { parseDate, today, getLocalTimeZone, CalendarDate } from '@internationalized/date';
import { CCProviderModelRegistryEntry, ModelProvider, ModelRegistryType } from '../../types';
import { fetchProviderModelRegistry, setRegistryDeprecation } from '../../services/api';
import type { SortOptions } from '../../services/api/paginationParams';
import { ColumnDefinition } from '../common/DataTable';
import { ActionsColumnHeader } from '../common/ActionsColumnHeader';
import { FilterableTable, FiltersState } from '../common/FilterableTable';
import { useProviderMetadata } from '../../hooks/useProviderMetadata';
import { modelTypeFilterOptions, yesNoFilterOptions, getModelValueLabel } from '../../constants/modelOptions';
import { ThirdPartyModelDisclaimer } from '../common/ThirdPartyModelDisclaimer';
import { TablePanel } from '../common/TablePanel';
import '../Models/modelsTableScroll.css';
function toCalendarDate(iso: string | null): CalendarDate | null {
    if (!iso) return null;
    try {
        return parseDate(iso);
    } catch  {
        return null;
    }
}
function isRowDirty(entry: CCProviderModelRegistryEntry, saved: CCProviderModelRegistryEntry | undefined): boolean {
    if (!saved) return false;
    return entry.isDeprecated !== saved.isDeprecated || entry.sunsetDate !== saved.sunsetDate;
}
// A deprecated row isn't savable until it also has a sunset date — the two
// fields are set together via the Switch/DatePicker above. Un-deprecating
// needs no date at all, so it's never blocked by this.
function isRowSavable(entry: CCProviderModelRegistryEntry): boolean {
    return !entry.isDeprecated || !!entry.sunsetDate;
}
export const ModelRegistryTable: React.FC = ()=>{
    const { t } = useTranslation();
    const { providers } = useProviderMetadata();
    const [entries, setEntries] = useState<CCProviderModelRegistryEntry[]>([]);
    // Snapshot each row's draft is compared against — a toggle/date edit is
    // only local until Save is pressed, matching the registry's real-world
    // "don't silently persist" requirement.
    const [savedEntries, setSavedEntries] = useState<Record<string, CCProviderModelRegistryEntry>>({});
    const [loading, setLoading] = useState(true);
    const [filterValues, setFilterValues] = useState<FiltersState>({});
    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>();
    // Latest-wins guard: rapid filter/sort changes fire overlapping fetches;
    // only the most recent one is allowed to apply its result.
    const loadSeqRef = useRef(0);
    // provider/type/isDeprecated are exact-match selects in the UI, and the
    // backend's FILTERABLE_COLUMNS is equality-only — those three (plus sort)
    // go server-side. "model" (free text) and "sunsetDate" (a range) aren't
    // equality-compatible, so they stay client-side, applied on top of
    // whatever the server already filtered/sorted below.
    const serverFilters = useMemo(()=>({
            provider: (filterValues.provider as ModelProvider) || undefined,
            type: (filterValues.type as ModelRegistryType) || undefined,
            isDeprecated: filterValues.isDeprecated ? filterValues.isDeprecated === 'true' : undefined
        }), [
        filterValues.provider,
        filterValues.type,
        filterValues.isDeprecated
    ]);
    // Only provider/model/type are sortable here, and their column keys are
    // already the wire/DB names, so no FE→wire remap is needed.
    const serverSort = useMemo<SortOptions | undefined>(()=>{
        if (!sortDescriptor?.column) return undefined;
        return {
            column: String(sortDescriptor.column),
            direction: sortDescriptor.direction === 'descending' ? 'descending' : 'ascending'
        };
    }, [
        sortDescriptor
    ]);
    const loadEntries = useCallback(async ()=>{
        const seq = ++loadSeqRef.current;
        setLoading(true);
        try {
            const registryEntries = await fetchProviderModelRegistry(serverFilters, serverSort);
            if (seq !== loadSeqRef.current) return; // a newer load supersedes this one
            setEntries(registryEntries);
            setSavedEntries(Object.fromEntries(registryEntries.map((entry)=>[
                    entry.id,
                    entry
                ])));
        } catch (err) {
            if (seq !== loadSeqRef.current) return;
            toast.negative(err instanceof Error ? err.message : t('commandCenter.modelRegistry.failedToLoad'));
        } finally{
            if (seq === loadSeqRef.current) setLoading(false);
        }
    }, [
        serverFilters,
        serverSort,
        t
    ]);
    useEffect(()=>{
        loadEntries();
    }, [
        loadEntries
    ]);
    const updateLocalEntry = useCallback((id: string, patch: Partial<CCProviderModelRegistryEntry>)=>{
        setEntries((prev)=>prev.map((entry)=>(entry.id === id ? {
                    ...entry,
                    ...patch
                } : entry)));
    }, []);
    const handleSaveRow = useCallback(async (entry: CCProviderModelRegistryEntry)=>{
        try {
            const updated = await setRegistryDeprecation(entry);
            setEntries((prev)=>prev.map((e)=>(e.id === updated.id ? updated : e)));
            setSavedEntries((prev)=>({
                    ...prev,
                    [updated.id]: updated
                }));
            toast.positive(t('commandCenter.modelRegistry.updated', {
                model: entry.model
            }));
        } catch (err) {
            // Leave the row dirty so the user can retry; a failed save must not
            // look like it succeeded.
            toast.negative(err instanceof Error ? err.message : t('commandCenter.modelRegistry.failedToSave'));
        }
    }, [
        t
    ]);
    const handleCancelRow = useCallback((entry: CCProviderModelRegistryEntry)=>{
        const saved = savedEntries[entry.id];
        if (saved) {
            setEntries((prev)=>prev.map((e)=>(e.id === entry.id ? saved : e)));
        }
    }, [
        savedEntries
    ]);
    const columns: ColumnDefinition<CCProviderModelRegistryEntry>[] = useMemo(()=>[
            {
                key: 'provider',
                label: t('commandCenter.modelRegistry.columns.provider'),
                minWidth: 140,
                allowsSorting: true,
                allowsResizing: true,
                render: (entry)=>{
                    const label = providers.find((p)=>p.key === entry.provider)?.label ?? entry.provider;
                    return <Text>{label}</Text>;
                }
            },
            {
                key: 'model',
                label: t('commandCenter.modelRegistry.columns.model'),
                minWidth: 160,
                allowsSorting: true,
                allowsResizing: true,
                isRowHeader: true
            },
            {
                key: 'type',
                label: t('commandCenter.modelRegistry.columns.type'),
                minWidth: 120,
                allowsSorting: true,
                allowsResizing: true,
                render: (entry)=><Text>{getModelValueLabel(entry.type, t)}</Text>
            },
            {
                key: 'isDeprecated',
                label: t('commandCenter.modelRegistry.columns.deprecated'),
                minWidth: 140,
                render: (entry)=>(<Switch isSelected={entry.isDeprecated} onChange={(isSelected)=>updateLocalEntry(entry.id, {
                            isDeprecated: isSelected,
                            sunsetDate: isSelected ? entry.sunsetDate : null
                        })} aria-label={t('commandCenter.modelRegistry.toggleDeprecated', {
                        model: entry.model
                    })}/>)
            },
            {
                key: 'sunsetDate',
                label: t('commandCenter.modelRegistry.columns.deprecationDate'),
                minWidth: 180,
                render: (entry)=>{
                    if (!entry.isDeprecated) {
                        return <Text UNSAFE_style={{
                            color: 'var(--spectrum-global-color-gray-500)'
                        }}>—</Text>;
                    }
                    const sunset = toCalendarDate(entry.sunsetDate);
                    const savedSunset = toCalendarDate(savedEntries[entry.id]?.sunsetDate ?? null);
                    const todayDate = today(getLocalTimeZone());
                    // Warn only for a past date the user just set; a stale past value
                    // that came unchanged from the API (matches the saved snapshot) is
                    // left un-flagged. minValue keeps the calendar to today-onwards.
                    const isPastEdit = !!sunset && sunset.compare(todayDate) < 0 && (!savedSunset || sunset.compare(savedSunset) !== 0);
                    return (<DatePicker aria-label={t('commandCenter.modelRegistry.lastDate', {
                        model: entry.model
                    })} value={sunset} onChange={(date)=>updateLocalEntry(entry.id, {
                            sunsetDate: date ? (date as CalendarDate).toString() : null
                        })} minValue={todayDate} isInvalid={isPastEdit}/>);
                }
            },
            {
                key: 'actions',
                label: t('commandCenter.modelRegistry.columns.actions'),
                minWidth: 100,
                renderHeader: ()=><ActionsColumnHeader/>,
                render: (entry)=>isRowDirty(entry, savedEntries[entry.id]) ? (<div className={" sd171 Ue171 qe171"}>
              <ActionButton isQuiet aria-label={t('commandCenter.modelRegistry.cancelChanges', {
                        model: entry.model
                    })} onPress={()=>handleCancelRow(entry)}>
                <Close/>
              </ActionButton>
              <ActionButton isQuiet aria-label={t('commandCenter.modelRegistry.saveRow', {
                        model: entry.model
                    })} isDisabled={!isRowSavable(entry)} onPress={()=>handleSaveRow(entry)}>
                <Checkmark/>
              </ActionButton>
            </div>) : null
            }
        ], [
        providers,
        savedEntries,
        updateLocalEntry,
        handleSaveRow,
        handleCancelRow,
        t
    ]);
    // Provider options are derived from live data (metadata), never a
    // hardcoded list — Type is a genuine fixed 2-value enum, so that one's a
    // static dropdown. Model stays free text: the registry can grow large
    // enough that a dropdown of every model name stops being usable.
    const filterConfigs = useMemo(()=>{
        const providerOptions = providers.map((p)=>({
                value: p.key,
                label: p.label
            }));
        return [
            {
                key: 'provider',
                label: t('commandCenter.modelRegistry.filters.provider'),
                type: 'select' as const,
                options: providerOptions
            },
            {
                key: 'model',
                label: t('commandCenter.modelRegistry.filters.model'),
                placeholder: t('commandCenter.modelRegistry.searchByModel')
            },
            {
                key: 'type',
                label: t('commandCenter.modelRegistry.filters.type'),
                type: 'select' as const,
                options: modelTypeFilterOptions(t)
            },
            {
                key: 'isDeprecated',
                label: t('commandCenter.modelRegistry.filters.deprecated'),
                type: 'select' as const,
                options: yesNoFilterOptions(t)
            },
            {
                key: 'sunsetDate',
                label: t('commandCenter.modelRegistry.filters.deprecationDate'),
                type: 'range_date' as const
            }
        ];
    }, [
        providers,
        t
    ]);
    // provider/type/isDeprecated and sort are already applied server-side
    // (see serverFilters/serverSort above) — only the two filters the backend
    // can't do an equality match on are left to apply here, over the rows the
    // server already returned.
    const filteredEntries = useMemo(()=>{
        const modelFilter = (filterValues.model || '').toLowerCase();
        return entries.filter((entry)=>{
            if (modelFilter && !entry.model.toLowerCase().includes(modelFilter)) return false;
            if (filterValues.sunsetDate) {
                const [min, max] = filterValues.sunsetDate.split('..');
                if (!entry.sunsetDate) return false;
                if (min && entry.sunsetDate < min) return false;
                if (max && entry.sunsetDate > max) return false;
            }
            return true;
        });
    }, [
        entries,
        filterValues.model,
        filterValues.sunsetDate
    ]);
    return (<TablePanel className="models-table-stack">
      <FilterableTable<CCProviderModelRegistryEntry> filterConfigs={filterConfigs} filterValues={filterValues} onFiltersChange={setFilterValues} onFiltersClear={()=>setFilterValues({})} items={filteredEntries} columns={columns} getRowKey={(entry)=>entry.id} sortDescriptor={sortDescriptor} onSortChange={setSortDescriptor} loading={loading} ariaLabel={t('commandCenter.modelRegistry.ariaLabel')} emptyStateTitle={t('commandCenter.modelRegistry.emptyTitle')} emptyStateDescription={t('commandCenter.modelRegistry.emptyDescription')} layout="fill" configurableColumns/>

      <ThirdPartyModelDisclaimer/>
    </TablePanel>);
};

import "macro-24f4ab2c310524061affe4f95ef9b55a0b42469a5a8444ed21a41727e0f1cf87.css";